/**
 * <SamConversation> — la conversation avec Sam, sur une activité (organism).
 *
 * Ce que la feuille du téléphone et le panneau latéral du web montrent (`<SamSheet>` les porte) :
 * une ligne d'en-tête, puis le socle de conversation (`<ConversationPanel>`) habillé des états
 * de Sam. Elle ne dessine ni fond ni cadre.
 *
 * Tous ses états arrivent en props, et chacun s'ouvre seul dans une story :
 *  - `welcome` : rien ne s'est dit, Sam se présente et propose ses cartes de démarrage ;
 *  - le fil (`days`), où Sam parle en bulle avec sa tête (`<ChampionAvatar name="sam">`) ;
 *  - `thinking` : ses points, et « Sam réfléchit… » dessous ;
 *  - `workCheck` : il lit la photo du travail (`reading`, puis `late` au-delà de neuf secondes) ;
 *  - `cap` : un des deux plafonds est atteint (15 messages sur l'activité, 30 dans la journée) :
 *    la bulle que le serveur envoie le dit, les suggestions s'estompent, le composeur se ferme et
 *    dit quand Sam revient (« Sam revient à la prochaine activité », « Sam revient demain »). Pas
 *    de bandeau : la chose ne se dit pas trois fois. À l'accueil d'une autre activité, Sam se
 *    présente sans proposer ses cartes ;
 *  - `slow` : Sam n'a pas répondu ; l'élève peut continuer d'écrire, ou finir sans lui.
 * Un message qui n'est pas parti est marqué dans le fil et se renvoie (`onRetryMessage`).
 *
 * Ce que Sam dit sans le modèle (son bonjour, un message trop long, une photo illisible…) n'est
 * pas un état : ce sont des bulles du fil, comme les autres.
 *
 * Sam n'a pas de couleur ici : l'envoi, les suggestions et la bulle de l'élève sont ceux de
 * l'univers de l'élève ; le bleu de Sam ne peint que son nœud.
 *
 * L'en-tête ne redit pas « Sam réfléchit… » : la phrase se lit une fois, sous ses points.
 * Pendant l'accueil, l'en-tête ne garde que la croix : le grand Sam juste dessous porte déjà
 * son nom.
 *
 * Composes DS pieces only — `<ConversationHeader>`, `<ConversationPanel>`, `<SamWelcome>`,
 * `<SamWorkCheckWait>`, `<ChampionAvatar>`, `<Banner>`, `<ConversationClose>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { Banner } from "../../atoms/Banner"
import {
    CONVERSATION_HEADER_AVATAR_SIZE,
    ConversationClose,
    ConversationHeader,
} from "../../molecules/ConversationHeader"
import { type UIConversationMessage } from "../../molecules/MessageRow"
import { type UIQuickReply } from "../../molecules/QuickReplies"
import { SamWelcome, type UISamStarter } from "../../molecules/SamWelcome"
import { SamWorkCheckWait } from "../../molecules/SamWorkCheckWait"
import { ConversationPanel } from "../ConversationPanel"
import { SAM_INTERLOCUTOR, type UIConversationDay } from "../ConversationThread"

/** Lequel des deux plafonds : celui de l'activité, ou celui de la journée (toutes les activités). */
export type SamCap = "activity" | "daily"

/** Sam lit une photo du travail : `reading`, puis `late` quand ça dure. */
export type SamWorkCheck = "reading" | "late"

export type SamConversationProps = {
    /** Le fil, groupé sans jour ni heure (`groupConversation(messages, { dated: false })`). */
    days: UIConversationDay[]
    /** Rien ne s'est encore dit : l'accueil tient la place du fil. Défaut `false`. */
    welcome?: boolean
    /** Les cartes de démarrage de l'accueil. */
    starters?: UISamStarter[]
    onStarter?: (key: string) => void

    /** Sam réfléchit. Défaut `false`. */
    thinking?: boolean
    /** Sam lit une photo du travail. */
    workCheck?: SamWorkCheck | null
    /** Un plafond de messages est atteint. */
    cap?: SamCap | null
    /** Sam met du temps à répondre. Sans effet sous un plafond, qui le dit déjà. Défaut `false`. */
    slow?: boolean

    /** Les suggestions rapides d'une conversation lancée. Masquées pendant l'accueil. */
    quickReplies?: UIQuickReply[]
    onQuickReply?: (key: string) => void

    draft: string
    onChangeDraft: (text: string) => void
    onSend: () => void

    onClose: () => void
    onRetryMessage?: (message: UIConversationMessage) => void
    onOpenAttachment?: (message: UIConversationMessage) => void
    testID?: string
}

const TEXTS = {
    thinking: "Sam réfléchit…",
    placeholder: "Écris à Sam…",
    close: "Fermer la conversation",
    slow: "Sam met du temps à répondre. Tu peux continuer l'activité sans lui.",
    capComposer: {
        activity: "Sam revient à la prochaine activité",
        daily: "Sam revient demain",
    },
}

/** Le serveur refuse au-delà. */
const MESSAGE_MAX_LENGTH = 2000

const NO_DAYS: UIConversationDay[] = []
const NO_STARTERS: UISamStarter[] = []

export function SamConversation(props: SamConversationProps) {
    const {
        days,
        welcome = false,
        starters = [],
        onStarter,
        thinking = false,
        workCheck,
        cap,
        slow = false,
        quickReplies = [],
        onQuickReply,
        draft,
        onChangeDraft,
        onSend,
        onClose,
        onRetryMessage,
        onOpenAttachment,
        testID,
    } = props

    const capped = cap != null
    const reading = workCheck != null

    // Sous un plafond, pas de bandeau : la bulle de Sam et le composeur fermé le disent déjà.
    // Marine : l'orange d'alerte écrit sur sa tinte ne tient pas le contraste.
    const notice =
        slow && !capped ? <Banner message={TEXTS.slow} icon="signal" color="primary" align="start" tinted /> : undefined

    return (
        <View style={styles.root} testID={testID}>
            <ConversationHeader
                bare
                name={welcome ? "" : SAM_INTERLOCUTOR.name}
                avatar={welcome ? undefined : <ChampionAvatar name="sam" size={CONVERSATION_HEADER_AVATAR_SIZE} />}
                end={<ConversationClose onPress={onClose} accessibilityLabel={TEXTS.close} />}
            />

            <ConversationPanel
                thread={{
                    // L'accueil remplace le fil, même quand Sam a déjà glissé son bonjour en aperçu.
                    days: welcome ? NO_DAYS : days,
                    interlocutor: SAM_INTERLOCUTOR,
                    // Sous un plafond, pas de carte : quatre cartes grisées n'apprennent rien, le bandeau
                    // dit pourquoi Sam ne répond pas.
                    empty: welcome ? (
                        <SamWelcome starters={capped ? NO_STARTERS : starters} onSelect={(key) => onStarter?.(key)} />
                    ) : undefined,
                    // La lecture d'une photo se raconte dans sa propre bulle : pas de points en plus.
                    typingLabel: thinking && !reading && !welcome ? TEXTS.thinking : undefined,
                    footer: reading && !welcome ? <SamWorkCheckWait late={workCheck === "late"} /> : undefined,
                    onRetryMessage,
                    onOpenAttachment,
                }}
                notice={notice}
                quickReplies={
                    welcome || quickReplies.length === 0
                        ? undefined
                        : {
                              replies: quickReplies,
                              onSelect: (key) => onQuickReply?.(key),
                              // Estompées, jamais retirées : le bas de la feuille ne saute pas.
                              disabled: thinking || capped,
                          }
                }
                composer={{
                    value: draft,
                    onChangeText: onChangeDraft,
                    onSend,
                    placeholder: TEXTS.placeholder,
                    maxLength: MESSAGE_MAX_LENGTH,
                    disabledReason: cap != null ? TEXTS.capComposer[cap] : undefined,
                }}
            />
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    root: { flex: 1, minHeight: 0 },
}))
